import React from "react";

/**
 * ApplicationDetails.jsx
 *
 * Minimal changes:
 *  - no sample/test data included
 *  - if no data (from options[0] or `data` prop) -> show a friendly placeholder
 *  - otherwise behavior and layout unchanged
 */

export default function ApplicationDetails({ type, options, data }) {
  const appData = (options ?? [])[0] ?? {};

  const viewData = React.useMemo(() => {
    const isEmptyObject = (o) =>
      !o || typeof o !== "object" || Object.keys(o).length === 0;

    // prefer appData over data
    const source = !isEmptyObject(appData) ? appData : data ?? {};

    if (isEmptyObject(source)) return { empty: true };

    // Helper: produce rows from an array of objects but skip title/Title keys.
    const toRows = (arr = []) => {
      if (!Array.isArray(arr)) return [];
      return arr.flatMap((obj) => {
        if (!obj || typeof obj !== "object") return [];
        return Object.entries(obj)
          .filter(([k]) => k !== "title" && k !== "Title")
          .map(([k, v]) => ({ [k]: v }));
      });
    };

    // Helper: extract first available title from the array (title or Title)
    const extractTitle = (arr = [], fallback) => {
      if (!Array.isArray(arr)) return fallback;
      const found = arr.find((o) => o && (o.title || o.Title));
      return found ? found.title ?? found.Title : fallback;
    };

    const sections = [];

    if (Array.isArray(source.Sections) && source.Sections.length > 0) {
      sections.push({
        id: "sections",
        title: extractTitle(source.Sections, "SECTIONS"),
        rows: toRows(source.Sections),
      });
    }

    if (Array.isArray(source.Others) && source.Others.length > 0) {
      sections.push({
        id: "others",
        title: extractTitle(source.Others, "OTHERS"),
        rows: toRows(source.Others),
      });
    }

    // Fallback: use top-level keys as one section (exclude common metadata keys)
    if (sections.length === 0) {
      const meta = new Set([
        "Title",
        "Subtitle",
        "title",
        "subtitle",
        "Name",
        "name",
        "layoutid",
        "target",
        "targetid",
        "type",
      ]);

      const remaining = Object.keys(source).filter((k) => !meta.has(k));
      if (remaining.length > 0) {
        sections.push({
          id: "profile",
          title: "DETAILS",
          rows: remaining.map((k) => ({ [k]: source[k] })),
        });
      }
    }

    return {
      empty: false,
      title: source.Title ?? source.title,
      subtitle: source.Subtitle ?? source.subtitle,
      companyTitle: source.name ?? source.Name,
      sections,
    };
  }, [appData, data]);

  console.log("the viewData", viewData);

  if (
    viewData.empty ||
    !Array.isArray(viewData.sections) ||
    viewData.sections.length === 0
  ) {
    return (
      <div className="min-h-screen bg-gray-50 p-6 text-slate-700 w-full flex items-center justify-center">
        <div className="max-w-md w-full text-center bg-white rounded-md shadow-sm p-8">
          <div className="mx-auto w-20 h-20 flex items-center justify-center rounded-full bg-gray-100 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-10 h-10 text-gray-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                d="M3 7v10a2 2 0 0 0 2 2h14"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16 3H8v4h8V3z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 11h8M8 15h5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <h3 className="text-lg font-semibold text-slate-700">
            No data found
          </h3>
          <p className="mt-2 text-sm text-slate-500">
            We couldn’t find any application details to display. If you were
            expecting data, check your source or try reloading.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 text-slate-700 w-full overflow-y-auto">
      <div className="w-full mx-auto bg-white shadow-sm rounded-md">
        <div className="px-8 py-10">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-wider text-slate-700">
              {viewData.title}
            </h2>
            {viewData.subtitle && (
              <p className="mt-1 text-sm text-slate-500">{viewData.subtitle}</p>
            )}
          </div>

          <div className="mt-8">
            {viewData.companyTitle && (
              <h3 className="text-slate-600 font-semibold tracking-wide">
                {viewData.companyTitle}
              </h3>
            )}
          </div>

          <div className="mt-4 space-y-6">
            {viewData.sections.map((section) => (
              <Section key={section.id} section={section} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ section }) {
  return (
    <section className="bg-white border rounded-md">
      <div className="bg-primary text-white py-3 px-4 font-semibold tracking-wide uppercase">
        {section.title}
      </div>

      <div className="p-4">
        <div className="divide-y">
          {section.rows.map((row, idx) => {
            const entries = Object.entries(row);

            const normalized = entries.map(([k, v]) => [
              k,
              v === undefined || v === null || String(v).trim() === ""
                ? "-"
                : v,
            ]);

            if (normalized.length === 1) {
              const [label, value] = normalized[0];
              return (
                <div
                  key={idx}
                  className="py-4 grid grid-cols-12 gap-4 items-start"
                >
                  <div className="col-span-3 text-sm text-slate-600">
                    {label}:
                  </div>
                  <div className="col-span-9 text-sm font-semibold text-slate-700">
                    {value}
                  </div>
                </div>
              );
            }

            if (normalized.length === 2) {
              return (
                <div
                  key={idx}
                  className="py-4 grid grid-cols-12 gap-4 items-start"
                >
                  {normalized.map(([label, value], i) => (
                    <React.Fragment key={i}>
                      <div className="col-span-3 text-sm text-slate-600">
                        {label}:
                      </div>
                      <div className="col-span-3 text-sm font-semibold text-slate-700">
                        {value}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              );
            }

            const cols = normalized.length;
            const colSpan = Math.max(2, Math.floor(12 / cols));

            return (
              <div
                key={idx}
                className="py-4 grid grid-cols-12 gap-4 items-start"
              >
                {normalized.map(([label, value], i) => (
                  <div key={i} className={`col-span-${colSpan} text-sm`}>
                    <div className="text-slate-600">{label}:</div>
                    <div className="mt-1 font-semibold text-slate-700">
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
